import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui';
import { useAppStore } from '../stores/appStore';
import { api, type PlanChangeQuote } from '../lib/api';
import { toast } from '../stores/toastStore';
import { motion } from 'framer-motion';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useTranslation } from 'react-i18next';

interface BillingPlan {
  code: string;
  name: string;
  bots: number;
  monthlyPriceUsd: number;
  stripePriceId: string;
  recommended: boolean;
  isAddon: boolean;
}

const SUBSCRIPTION_PENDING_LOTTIE_URL =
  'https://assets-v2.lottiefiles.com/a/e1dcab88-1166-11ee-ad4d-fbf063c74f5a/5mEVCmjSbi.lottie';
const SUBSCRIPTION_SUCCESS_LOTTIE_URL =
  'https://assets-v2.lottiefiles.com/a/44a48e59-f177-4bb5-9f07-f0992bb324dc/rHJp9kSEpe.lottie';

export function Subscription() {
  const { tenant, setTenant, bots } = useAppStore();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [processingPlanCode, setProcessingPlanCode] = useState<string | null>(null);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [isSyncingCheckout, setIsSyncingCheckout] = useState(false);
  const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(false);
  const [partnerContact, setPartnerContact] = useState<{ name: string; email: string } | null>(null);
  const [showPartnerManagedModal, setShowPartnerManagedModal] = useState(false);
  const [planChange, setPlanChange] = useState<{ plan: BillingPlan; quote: PlanChangeQuote } | null>(null);
  const [isApplyingPlanChange, setIsApplyingPlanChange] = useState(false);

  const currentLocale = i18n.language.toLowerCase().startsWith('pt') ? 'pt-BR' : 'en-US';
  const currencyFormatter = new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency: 'USD',
  });

  const getPlanFeatures = (plan: BillingPlan) => {
    if (plan.isAddon) {
      return [
        t('subscription.plans.addonExtraBot'),
        t('subscription.plans.addonMonthlyBilling'),
        t('subscription.plans.unlimitedMessages'),
        t('subscription.plans.bitrixIntegration'),
      ];
    }

    return [
      t('subscription.plans.includedBots', { count: plan.bots }),
      t('subscription.plans.unlimitedMessages'),
      t('subscription.plans.visualFlowBuilder'),
      t('subscription.plans.bitrixIntegration'),
    ];
  };

  useEffect(() => {
    api.getBillingConfig()
      .then((config) => setPlans(config.plans || []))
      .catch((error) => {
        console.error(error);
        toast.error(t('subscription.toasts.loadPlansError'));
      })
      .finally(() => setIsLoadingPlans(false));
  }, [t]);

  useEffect(() => {
    const syncCheckoutSuccess = async () => {
      if (!tenant?.id) return;

      const params = new URLSearchParams(window.location.search);
      const checkoutState = params.get('checkout');
      if (checkoutState !== 'success') {
        return;
      }

      const isPaymentBlockedStatus =
        tenant.status === 'suspended' || tenant.status === 'cancelled';

      setShowCheckoutSuccess(true);

      if (!isPaymentBlockedStatus) {
        toast.success(t('subscription.toasts.paymentConfirmed'));
        params.delete('checkout');
        const nextQuery = params.toString();
        const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
        window.history.replaceState({}, '', nextUrl);
        return;
      }

      setIsSyncingCheckout(true);

      try {
        let syncedTenant = await api.syncStripeBillingStatus(tenant.id);

        if (syncedTenant.status !== 'active') {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          syncedTenant = await api.syncStripeBillingStatus(tenant.id);
        }

        setTenant(syncedTenant);
        toast.success(t('subscription.toasts.paymentReleased'));
      } catch (error) {
        console.error(error);
        toast.error(t('subscription.toasts.paymentReturnReceived'));
      } finally {
        params.delete('checkout');
        const nextQuery = params.toString();
        const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
        window.history.replaceState({}, '', nextUrl);
        setIsSyncingCheckout(false);
      }
    };

    void syncCheckoutSuccess();
  }, [tenant?.id, setTenant, t]);

  const isTrial = tenant?.status === 'trial';
  const isExpired = tenant?.status === 'expired';
  const isPaymentPending = tenant?.status === 'suspended' || tenant?.status === 'cancelled';
  const isPartnerTenant = !!tenant?.isPartner;
  const isPartnerManagedClient = !!tenant?.partnerTenantId;
  const botCount = bots.length;
  const billingSettings = (tenant?.settings?.billing || {}) as Record<string, any>;
  const baseBotsFromBilling = Number(billingSettings.baseBots || 0);
  const additionalBotsFromBilling = Number(billingSettings.additionalBots || 0);
  const baseBotsFromPlan =
    tenant?.subscriptionPlan === 'starter'
      ? 1
      : tenant?.subscriptionPlan === 'professional' || tenant?.subscriptionPlan === 'enterprise'
      ? 2
      : 0;
  const normalizedBaseBots = Math.max(
    0,
    Number.isFinite(baseBotsFromBilling) && baseBotsFromBilling > 0
      ? baseBotsFromBilling
      : baseBotsFromPlan,
  );
  const inferredBaseBotsFromActiveBots = botCount >= 2 ? 2 : botCount >= 1 ? 1 : 0;
  const currentBasePlanBots =
    normalizedBaseBots >= 2
      ? 2
      : normalizedBaseBots >= 1
      ? 1
      : inferredBaseBotsFromActiveBots;
  const currentAdditionalBots =
    Number.isFinite(additionalBotsFromBilling) && additionalBotsFromBilling > 0
      ? additionalBotsFromBilling
      : 0;
  const hasPaidSubscription =
    !!tenant?.stripeSubscriptionId ||
    tenant?.subscriptionPlan === 'starter' ||
    tenant?.subscriptionPlan === 'professional' ||
    tenant?.subscriptionPlan === 'enterprise';
  const isTrialBlocked =
    tenant?.status === 'trial' &&
    !!tenant?.trialEndsAt &&
    new Date(tenant.trialEndsAt).getTime() <= Date.now();
  const shouldShowPartnerContactScreen =
    isPartnerManagedClient && (isExpired || isPaymentPending || isTrialBlocked);

  const visiblePlans = useMemo(() => {
    if (isPartnerTenant) {
      return [];
    }

    return plans
      .filter((plan) => plan.isAddon || plan.bots === 1 || plan.bots === 2)
      .filter((plan) => {
        if (!hasPaidSubscription || plan.isAddon) {
          return true;
        }

        // On paid accounts, we keep only the current tier and possible upgrades.
        if (currentBasePlanBots <= 1) {
          return true;
        }

        return plan.bots >= currentBasePlanBots;
      });
  }, [isPartnerTenant, plans, hasPaidSubscription, currentBasePlanBots]);

  const formatMoney = (cents: number, currency: string) =>
    new Intl.NumberFormat(currentLocale, {
      style: 'currency',
      currency: (currency || 'brl').toUpperCase(),
    }).format((cents || 0) / 100);

  const formatRenewalDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(currentLocale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  const isCurrentPlan = (plan: BillingPlan) => {
    if (!hasPaidSubscription) {
      return false;
    }

    if (plan.isAddon) {
      return currentAdditionalBots > 0;
    }

    if (currentBasePlanBots === 1) {
      return plan.bots === 1;
    }

    if (currentBasePlanBots >= 2) {
      return plan.bots === 2;
    }

    return false;
  };

  useEffect(() => {
    const loadPartnerContact = async () => {
      if (!tenant?.partnerTenantId) {
        setPartnerContact(null);
        return;
      }

      try {
        const partnerTenant = await api.getTenant(tenant.partnerTenantId);
        setPartnerContact({
          name: partnerTenant.name,
          email: partnerTenant.settings?.technicalResponsibleEmail || partnerTenant.email,
        });
      } catch {
        setPartnerContact(null);
      }
    };

    void loadPartnerContact();
  }, [tenant?.partnerTenantId]);

  useEffect(() => {
    if (isPaymentPending) {
      sessionStorage.setItem('billingBlockedView', '1');
    }
  }, [isPaymentPending]);

  useEffect(() => {
    if (!tenant?.id || tenant.status !== 'active') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const cameFromCheckoutSuccess = params.get('checkout') === 'success';
    const cameFromBlockedView = sessionStorage.getItem('billingBlockedView') === '1';

    if (!showCheckoutSuccess && !cameFromCheckoutSuccess && !cameFromBlockedView) {
      return;
    }

    if (!showCheckoutSuccess && cameFromBlockedView) {
      setShowCheckoutSuccess(true);
      return;
    }

    const redirectDelayMs = showCheckoutSuccess ? 2000 : 300;
    const timeoutId = window.setTimeout(() => {
      sessionStorage.removeItem('billingBlockedView');
      navigate('/', { replace: true });
    }, redirectDelayMs);

    return () => window.clearTimeout(timeoutId);
  }, [tenant?.id, tenant?.status, showCheckoutSuccess, navigate]);
  
  const trialDaysLeft = tenant?.trialEndsAt 
    ? Math.ceil((new Date(tenant.trialEndsAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  const openStripeInNewTab = (url: string) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');

    if (newWindow) {
      return;
    }

    window.prompt(t('subscription.browserBlockedPrompt'), url);
  };

  const handleCheckout = async (plan: BillingPlan) => {
    if (!tenant?.id) return;

    setProcessingPlanCode(plan.code);
    try {
      const session = await api.createStripeCheckoutSession(tenant.id, plan.stripePriceId);
      openStripeInNewTab(session.url);
    } catch (error) {
      console.error(error);
      toast.error(t('subscription.toasts.checkoutStartError'));
    } finally {
      setProcessingPlanCode(null);
    }
  };

  const handlePlanAction = async (plan: BillingPlan) => {
    if (plan.isAddon && botCount < 2) {
      return;
    }

    if (isPartnerManagedClient) {
      setShowPartnerManagedModal(true);
      return;
    }

    // Already paying? Change the existing subscription instead of opening a new
    // checkout — a second subscription would bill the customer twice.
    if (hasPaidSubscription) {
      await handlePlanChangePreview(plan);
      return;
    }

    await handleCheckout(plan);
  };

  const handlePlanChangePreview = async (plan: BillingPlan) => {
    if (!tenant?.id) return;

    setProcessingPlanCode(plan.code);
    try {
      const quote = await api.previewPlanChange(tenant.id, plan.stripePriceId, 1);
      if (quote.mode === 'unchanged') {
        toast.info('Este já é o seu plano atual.');
        return;
      }
      setPlanChange({ plan, quote });
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || 'Não foi possível calcular o valor da alteração.');
    } finally {
      setProcessingPlanCode(null);
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!tenant?.id || !planChange || isApplyingPlanChange) return;

    setIsApplyingPlanChange(true);
    try {
      const result = await api.changePlan(tenant.id, planChange.plan.stripePriceId, 1);
      setPlanChange(null);

      toast.success(
        result.mode === 'upgrade'
          ? `Plano alterado. Cobramos ${formatMoney(result.immediateChargeCents, result.currency)} agora.`
          : `Alteração agendada. O novo valor passa a valer em ${formatRenewalDate(result.nextRenewalAt)}.`,
        'Plano atualizado',
        8000,
      );

      const syncedTenant = await api.syncStripeBillingStatus(tenant.id);
      setTenant(syncedTenant);
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message || 'Não foi possível alterar o plano.', 'Alteração de plano', 9000);
    } finally {
      setIsApplyingPlanChange(false);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/');
  };

  const handleManageSubscription = async () => {
    if (!tenant?.id) return;

    setIsOpeningPortal(true);
    try {
      const session = await api.createStripePortalSession(tenant.id);
      openStripeInNewTab(session.url);
    } catch (error) {
      console.error(error);
      toast.error(t('subscription.toasts.openPortalError'));
    } finally {
      setIsOpeningPortal(false);
    }
  };

  if (showCheckoutSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.24, ease: 'easeOut' }}
        className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-4xl mx-auto min-h-[70vh] flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
            className="w-full max-w-2xl rounded-2xl border border-emerald-200 bg-emerald-50 p-8"
          >
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="h-28 w-28">
                <DotLottieReact
                  src={SUBSCRIPTION_SUCCESS_LOTTIE_URL}
                  loop
                  autoplay
                  className="h-full w-full"
                />
              </div>
              <h1 className="text-3xl font-bold text-emerald-900">
                {t('subscription.successScreen.title')}
              </h1>
              <p className="text-base text-emerald-800">
                {isSyncingCheckout
                  ? t('subscription.successScreen.syncing')
                  : t('subscription.successScreen.redirecting')}
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: 'easeOut' }}
      className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <Button variant="outline" onClick={handleBack}>
            <ChevronLeft className="mr-2 h-4 w-4" />
            {t('subscription.actions.back')}
          </Button>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            {shouldShowPartnerContactScreen
              ? t('subscription.header.partnerContactTitle')
              : isPaymentPending
              ? t('subscription.header.paymentPendingTitle')
              : isExpired
                ? t('subscription.header.expiredTitle')
                : t('subscription.header.defaultTitle')}
          </h1>
          <p className="text-xl text-slate-600">
            {shouldShowPartnerContactScreen
              ? t('subscription.header.partnerContactDescription')
              : isPaymentPending
              ? t('subscription.header.paymentPendingDescription')
              : isTrial 
                ? t('subscription.header.trialDescription', { count: Math.max(0, trialDaysLeft) })
                : t('subscription.header.defaultDescription')}
          </p>
          <div className="mt-4">
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ChevronLeft className="mr-2 h-4 w-4" />
              {t('subscription.actions.backToHome')}
            </Button>
          </div>
        </div>

        {shouldShowPartnerContactScreen && (
          <div className="max-w-3xl mx-auto mb-8 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-center">
            <p className="text-sm font-semibold text-amber-900">{t('subscription.partnerContact.title')}</p>
            <p className="text-sm text-amber-800 mt-1">
              {t('subscription.partnerContact.description')}
            </p>
            <div className="mt-3 text-sm text-amber-900">
              <p>
                {t('subscription.partnerContact.partnerLabel')} <strong>{partnerContact?.name || t('subscription.partnerContact.partnerFallback')}</strong>
              </p>
              <p>
                {t('subscription.partnerContact.contactLabel')} <strong>{partnerContact?.email || t('subscription.partnerContact.contactFallback')}</strong>
              </p>
            </div>
            {partnerContact?.email && (
              <div className="mt-4">
                <a href={`mailto:${partnerContact.email}`}>
                  <Button variant="outline">{t('subscription.partnerContact.action')}</Button>
                </a>
              </div>
            )}
          </div>
        )}

        {isPaymentPending && !shouldShowPartnerContactScreen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
            className="max-w-3xl mx-auto mb-8 rounded-2xl border border-amber-200 bg-amber-50 p-5"
          >
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="h-20 w-20">
                <DotLottieReact
                  src={SUBSCRIPTION_PENDING_LOTTIE_URL}
                  loop
                  autoplay
                  className="h-full w-full"
                />
              </div>
              <p className="text-sm font-medium text-amber-900">
                {t('subscription.pendingCard.description')}
              </p>
            </div>
          </motion.div>
        )}

        {showCheckoutSuccess && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
            className="max-w-3xl mx-auto mb-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
          >
            <div className="flex flex-col items-center gap-3 text-center">
              <div className="h-20 w-20">
                <DotLottieReact
                  src={SUBSCRIPTION_SUCCESS_LOTTIE_URL}
                  loop
                  autoplay
                  className="h-full w-full"
                />
              </div>
              <p className="text-sm font-medium text-emerald-900">
                {isSyncingCheckout
                  ? t('subscription.activeCard.syncing')
                  : t('subscription.activeCard.active')}
              </p>
            </div>
          </motion.div>
        )}

        {tenant?.stripeSubscriptionId && !isPartnerManagedClient && (
          <div className="max-w-3xl mx-auto mb-8 text-center">
            <Button onClick={handleManageSubscription} isLoading={isOpeningPortal} variant="outline">
              {t('subscription.actions.manageSubscription')}
            </Button>
          </div>
        )}

        {isPartnerManagedClient && !shouldShowPartnerContactScreen && (
          <div className="max-w-3xl mx-auto mb-8 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-center">
            <p className="text-sm font-semibold text-amber-900">{t('subscription.partnerManaged.title')}</p>
            <p className="text-sm text-amber-800 mt-1">
              {t('subscription.partnerManaged.description')}
            </p>
          </div>
        )}

        {isPartnerTenant ? (
          <div className="text-center text-slate-500">{t('subscription.partnerTenantUnavailable')}</div>
        ) : isPartnerManagedClient ? (
          <div className="text-center text-slate-500">{t('subscription.partnerManaged.hidePlansMessage')}</div>
        ) : isLoadingPlans ? (
          <div className="text-center text-slate-500">{t('subscription.plans.loading')}</div>
        ) : visiblePlans.length === 0 ? (
          <div className="text-center text-slate-500">{t('subscription.plans.empty')}</div>
        ) : (
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {visiblePlans.map((plan, index) => {
            const isAddonLocked = plan.isAddon && botCount < 2;
            const currentPlan = isCurrentPlan(plan);
            const isBaseDowngrade =
              hasPaidSubscription &&
              !plan.isAddon &&
              currentBasePlanBots > 0 &&
              plan.bots < currentBasePlanBots;
            const planFeatures = getPlanFeatures(plan);
            const shouldHidePrice = isPartnerManagedClient;
            const isActionDisabled = currentPlan || isAddonLocked || isBaseDowngrade;

            return (
            <motion.div
              key={plan.code}
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.24, delay: index * 0.05, ease: 'easeOut' }}
              className={`bg-white rounded-2xl shadow-xl overflow-hidden border-2 ${
                currentPlan
                  ? 'border-emerald-500'
                  : plan.recommended
                  ? 'border-blue-500 transform scale-105'
                  : 'border-transparent'
              }`}
            >
              {currentPlan && (
                <div className="bg-emerald-600 text-white text-center py-1 text-sm font-medium">
                  {t('subscription.plans.currentPlanBadge')}
                </div>
              )}
              {!currentPlan && plan.recommended && (
                <div className="bg-blue-500 text-white text-center py-1 text-sm font-medium">
                  {t('subscription.plans.popular')}
                </div>
              )}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                {!isAddonLocked && !shouldHidePrice && (
                  <>
                    <div className="flex items-baseline mb-6">
                      <span className="text-4xl font-bold text-slate-900">{currencyFormatter.format(plan.monthlyPriceUsd)}</span>
                      <span className="text-slate-500 ml-1">{t('subscription.plans.period')}</span>
                    </div>

                    <ul className="space-y-4 mb-8">
                      {planFeatures.map((feature) => (
                        <li key={feature} className="flex items-center text-slate-600">
                          <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {isAddonLocked && (
                  <div className="mb-8 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
                    {t('subscription.plans.addonLockedRequirement')}
                  </div>
                )}

                <Button 
                  className={`w-full ${plan.recommended ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-900 hover:bg-slate-800'}`}
                  onClick={() => void handlePlanAction(plan)}
                  isLoading={processingPlanCode === plan.code}
                  disabled={isActionDisabled}
                >
                  {isPartnerManagedClient
                    ? t('subscription.plans.partnerManagedButton')
                    : currentPlan
                    ? t('subscription.plans.currentPlanButton')
                    : isBaseDowngrade
                    ? t('subscription.plans.downgradeNotAvailableButton')
                    : isAddonLocked
                    ? t('subscription.plans.addonLockedButton')
                    : hasPaidSubscription
                    ? t('subscription.plans.upgradeButton')
                    : t('subscription.plans.subscribeButton')}
                </Button>
              </div>
            </motion.div>
            );
          })}
        </div>
        )}

        {!isPartnerManagedClient && !isPartnerTenant && (
        <div className="mt-12 text-center">
          <p className="text-slate-500">
            {t('subscription.support.prompt')} <a href="mailto:suporte@georanking.com.br" className="text-blue-600 hover:underline">{t('subscription.support.action')}</a>
          </p>
        </div>
        )}

        {planChange && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
              <h3 className="text-lg font-semibold text-slate-900">
                {planChange.quote.mode === 'upgrade' ? 'Confirmar upgrade' : 'Confirmar alteração'}
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                {planChange.plan.name} · {planChange.quote.nextBots}{' '}
                {planChange.quote.nextBots === 1 ? 'bot' : 'bots'}
              </p>

              <div className="mt-5 divide-y divide-slate-100 rounded-xl border border-slate-200">
                {planChange.quote.mode === 'upgrade' ? (
                  <>
                    <div className="flex items-baseline justify-between px-4 py-3">
                      <span className="text-sm text-slate-600">Você paga agora</span>
                      <span className="text-lg font-semibold text-slate-900">
                        {formatMoney(planChange.quote.immediateChargeCents, planChange.quote.currency)}
                      </span>
                    </div>
                    <p className="px-4 pb-3 -mt-2 text-xs text-slate-500">
                      Valor proporcional aos dias restantes até {formatRenewalDate(planChange.quote.nextRenewalAt)}.
                    </p>
                  </>
                ) : (
                  <div className="px-4 py-3 text-sm text-slate-600">
                    Nada é cobrado agora, e o valor já pago não é devolvido. Você mantém o plano atual até{' '}
                    {formatRenewalDate(planChange.quote.nextRenewalAt)}.
                  </div>
                )}
                <div className="flex items-baseline justify-between px-4 py-3">
                  <span className="text-sm text-slate-600">
                    A partir de {formatRenewalDate(planChange.quote.nextRenewalAt)}
                  </span>
                  <span className="text-sm font-medium text-slate-900">
                    {formatMoney(planChange.quote.nextCycleCents, planChange.quote.currency)} / mês
                  </span>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setPlanChange(null)}
                  disabled={isApplyingPlanChange}
                >
                  Cancelar
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => void handleConfirmPlanChange()}
                  isLoading={isApplyingPlanChange}
                >
                  {planChange.quote.mode === 'upgrade' ? 'Confirmar e pagar' : 'Confirmar alteração'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {showPartnerManagedModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4">
            <div className="w-full max-w-md rounded-2xl border border-amber-200 bg-white p-6 shadow-2xl">
              <h3 className="text-lg font-semibold text-slate-900">{t('subscription.partnerManaged.modalTitle')}</h3>
              <p className="mt-2 text-sm text-slate-600">
                {t('subscription.partnerManaged.modalDescription')}
              </p>
              <div className="mt-6 flex justify-end">
                <Button variant="outline" onClick={() => setShowPartnerManagedModal(false)}>
                  {t('subscription.partnerManaged.modalClose')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
